import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Player } from '@lottiefiles/react-lottie-player';
import { useLessonResult } from './useLessonResult';
import KPICards from './KPICards';
import QuestionSummary from './QuestionSummary';
import SummaryAnalytics from './SummaryAnalytics';
import Rate from '../../Rate';
import effectData from '../../../../Config/effect.json';
import './summary.css';

export default function LessonResult({
    lesson = {},
    lessonId,
    lessonTitle = '',
    lessonIndexId,
    questions = [],
    exam = {},
    user = {},
    elapsedSeconds = 0,
    isMission = false,
    fontSize = 18,
    onReset,
    onOpenQuestion,
    onClose,
    linkTo,
    hideLevel = false,
    hideReset = false,
    ViewGameResult = false
}) {
    const { t } = useTranslation('common');
    const history = useHistory();
    const [resetOpen, setResetOpen] = useState(false);
    const [rateModalOpen, setRateModalOpen] = useState(false);

    const isHideLevel = hideLevel;
    const isHideReset = hideReset || isMission;

    const {
        filteredResults,
        filterMode,
        setFilterMode,
        selectedLevel,
        setSelectedLevel,
        selectedSkill,
        setSelectedSkill,
        resetFilters,
        totalMax,
        totalUser,
        percentage,
        totalQuestions,
        solvedCount,
        correctCount,
        mistakesCount,
        unsolvedCount,
        resultState,
        resultText,
        chipTone,
        difficultyStats,
        skillsAnalysis,
        formattedTime,
        avgTimePerQuestion
    } = useLessonResult({
        questions,
        exam,
        elapsedSeconds,
        lesson
    });

    const headerIcon = (resultState === 'failed')
        ? 'fas fa-frown text-danger'
        : (resultState === 'average' || resultState === 'partial')
            ? 'fas fa-meh text-warning'
            : (resultState === 'excellent')
                ? 'fas fa-trophy text-warning'
                : (resultState === 'veryGood')
                    ? 'fas fa-medal text-success'
                    : 'fas fa-thumbs-up text-primary';

    const chipIcon = (resultState === 'failed')
        ? { icon: 'fa-exclamation-triangle', className: 'icon-fail' }
        : (resultState === 'average' || resultState === 'partial')
            ? { icon: 'fa-adjust', className: 'icon-partial' }
            : { icon: 'fa-check-circle', className: 'icon-success' };


    const confirmReset = () => {
        setResetOpen(false);
        if (typeof onReset === 'function') {
            onReset();
        } else {
            window.location.reload(false);
        }
    };

    const handleLeave = () => {
        if (linkTo && lessonIndexId) {
            history.push(`${linkTo}${lessonIndexId}`);
        } else if (lessonIndexId) {
            history.push(`/Tracks/Lessons/${lessonIndexId}`);
        } else {
            history.push('/Tracks/Lessons');
        }
    };

    const canRate = Boolean(!isMission && !lesson?.isRated && user?.user?.UserType && user?.user?.UserType?.Id !== 5);
    const celebrationEffect = effectData?.success?.[0]?.url || '/assets/effect/success/confetti.json';

    const hasActiveFilters = filterMode !== 'all' || selectedLevel !== null || selectedSkill !== null;

    return (
        <>
            {/* Lottie celebration if 100% - Play once, under the questions preview and skills */}
            {percentage === 100 && (
                <div
                    className="position-fixed w-100 d-flex align-items-center justify-content-center"
                    style={{
                        pointerEvents: 'none',
                        top: 0,
                        right: 0,
                        height: '100%',
                        width: '100%',
                        zIndex: 0,
                        overflow: 'hidden',
                        opacity: 0.6
                    }}
                >
                    <Player
                        autoplay
                        loop={false}
                        keepLastFrame={true}
                        src={celebrationEffect}
                        style={{ height: '100%', width: '100%' }}
                    />
                </div>
            )}

            {/* Reset Modal */}
            {resetOpen && (
                <div className="summary-modal" role="dialog" aria-modal="true" aria-labelledby="reset-modal-title">
                    <div className="summary-modal__backdrop" onClick={() => setResetOpen(false)} />
                    <div className="summary-modal__dialog">
                        <div className="summary-modal__header">
                            <i className="fas fa-redo-alt icon-accent text-primary ml-2"></i>
                            <div id="reset-modal-title" className="font-weight-bold">{t('Pages.Lessons.Summary.resetConfirmTitle') || t('Pages.Exams.ResetExam') || 'إعادة الامتحان'}</div>
                        </div>
                        <div className="summary-modal__body">
                            {t('Pages.Lessons.Summary.resetConfirmBody') || 'هل أنت متأكد من رغبتك في إعادة حل الدرس والامتحان؟ سيتم مسح الإجابات السابقة.'}
                        </div>
                        <div className="summary-modal__footer">
                            <button name="ok" className="topbar-btn modal-btn-ok" onClick={confirmReset}>
                                <i className="fas fa-check ml-1"></i>{t('confirm') || 'تأكيد'}
                            </button>
                            <button className="topbar-btn modal-btn-cancel" onClick={() => setResetOpen(false)}>
                                <i className="fas fa-times ml-1"></i>{t('cancel') || 'إلغاء'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rate Modal */}
            {rateModalOpen && (
                <Rate
                    id={lessonId}
                    IsForLesson={true}
                    showRate={() => setRateModalOpen(false)}
                    hideRate={() => setRateModalOpen(false)}
                />
            )}

            <div className="relative w-full h-full p-2" style={{ width: '100%', minHeight: '100%' }}>
                {/* Foreground content */}
                <div className="relative z-10 summary-panel rounded-xl w-full h-full p-3 overflow-hidden anim-rise flex flex-col">
                    {/* Top bar */}
                    <div className="summary-topbar flex items-center justify-between mb-3 anim-fade-up">
                        <h2 className="text-lg font-bold flex items-center gap-2">
                            <i className={`${headerIcon} ml-2`}></i>
                            {t('Pages.Lessons.Summary.title') || 'نتيجة الدرس والاختبار'}
                            {lessonTitle ? <span className="text-primary mr-1"> - {lessonTitle}</span> : null}
                        </h2>
                        <div className="topbar-actions flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-semibold status-chip blink ${chipTone}`}>
                                <i className={`fas ${chipIcon.icon} ${chipIcon.className} ml-1`}></i>
                                {resultText}
                            </span>
                            {canRate && (
                                <button className="topbar-btn cursor-pointer" onClick={() => setRateModalOpen(true)}>
                                    <i className="fas fa-star ml-1 text-warning"></i>{t('Pages.Lessons.tools.rate') || 'تقييم'}
                                </button>
                            )}
                            {!isHideReset && (
                                <button className="topbar-btn cursor-pointer" onClick={() => setResetOpen(true)}>
                                    <i className="fas fa-redo-alt ml-1 text-info"></i>{t('Pages.Exams.ResetExam') || 'إعادة'}
                                </button>
                            )}
                            {onClose ? (
                                <button className="topbar-btn cursor-pointer" onClick={onClose} title={t('Close') || 'إغلاق'}>
                                    <i className="fas fa-times ml-1 text-secondary"></i>{t('Close') || 'إغلاق'}
                                </button>
                            ) : (
                                <button className="topbar-btn cursor-pointer" onClick={handleLeave}>
                                    <i className="fas fa-sign-out-alt ml-1 text-secondary"></i>{t('Pages.Lessons.Summary.leaveLesson') || 'خروج'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* KPI Cards Grid */}
                    <KPICards
                        totalUser={totalUser}
                        totalMax={totalMax}
                        percentage={percentage}
                        formattedTime={formattedTime}
                        avgTimePerQuestion={avgTimePerQuestion}
                        solvedCount={solvedCount}
                        totalQuestions={totalQuestions}
                    />

                    {/* 2-Column Split: Left (Analytics & Skills) - Right (Rendered Questions) */}
                    <div className="flex-1" style={{ flex: 1 }}>
                        <div className="row" style={{ minHeight: '480px' }}>
                            {/* Left: analytics & skills (5 cols on lg) */}
                            <div className="col-12 col-lg-5 mb-3">
                                <div className="slim-scroll pr-1" style={{ maxHeight: '720px', overflowY: 'auto' }}>
                                    <SummaryAnalytics
                                        difficultyStats={difficultyStats}
                                        skillsAnalysis={skillsAnalysis}
                                        selectedLevel={selectedLevel}
                                        onSelectLevel={setSelectedLevel}
                                        selectedSkill={selectedSkill}
                                        onSelectSkill={setSelectedSkill}
                                        percentage={percentage}
                                        hideLevel={isHideLevel}
                                    />
                                </div>
                            </div>

                            {/* Right: rendered questions (7 cols on lg) */}
                            <div className="col-12 col-lg-7 mb-3 d-flex flex-column">
                                {/* Controls toolbar */}
                                <div className="summary-toolbar mb-2 print-hide anim-fade-up d-flex flex-wrap align-items-center" style={{ gap: '0.4rem' }}>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${filterMode === 'all' && !selectedLevel && !selectedSkill ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        style={{ fontSize: '0.78rem', borderRadius: '0.45rem' }}
                                        onClick={() => { setFilterMode('all'); setSelectedLevel(null); setSelectedSkill(null); }}
                                    >
                                        <i className="fas fa-list ml-1"></i>{t('Pages.Lessons.Summary.showAll') || t('showAll') || 'عرض الكل'} ({totalQuestions})
                                    </button>

                                    <button
                                        type="button"
                                        className={`btn btn-sm ${filterMode === 'solved' ? 'btn-success' : 'btn-outline-secondary'}`}
                                        style={{ fontSize: '0.78rem', borderRadius: '0.45rem' }}
                                        onClick={() => setFilterMode('solved')}
                                    >
                                        <i className="fas fa-check ml-1"></i>{t('Pages.Lessons.Summary.solvedOnly') || 'إجابات صحيحة'} ({correctCount})
                                    </button>

                                    <button
                                        type="button"
                                        className={`btn btn-sm ${filterMode === 'mistakes' ? 'btn-warning' : 'btn-outline-secondary'}`}
                                        style={{ fontSize: '0.78rem', borderRadius: '0.45rem' }}
                                        onClick={() => setFilterMode('mistakes')}
                                    >
                                        <i className="fas fa-bug ml-1"></i>{t('Pages.Lessons.Summary.mistakesOnly') || 'أخطاء'} ({mistakesCount})
                                    </button>

                                    <button
                                        type="button"
                                        className={`btn btn-sm ${filterMode === 'unsolved' ? 'btn-danger' : 'btn-outline-secondary'}`}
                                        style={{ fontSize: '0.78rem', borderRadius: '0.45rem' }}
                                        onClick={() => setFilterMode('unsolved')}
                                    >
                                        <i className="fas fa-times-circle ml-1"></i>{t('Pages.Lessons.Summary.unsolvedOnly') || 'غير مجابة'} ({unsolvedCount})
                                    </button>

                                    {/* Active Filter Chips */}
                                    {!isHideLevel && selectedLevel && (
                                        <span className="badge badge-info p-2 d-inline-flex align-items-center ml-1" style={{ fontSize: '0.76rem', gap: '0.3rem' }}>
                                            <i className="fas fa-layer-group"></i>
                                            <span>المستوى: {selectedLevel === 'easy' ? 'سهل' : selectedLevel === 'medium' ? 'متوسط' : 'صعب'}</span>
                                            <i className="fas fa-times cursor-pointer ml-1" onClick={() => setSelectedLevel(null)} title="إلغاء تصفية المستوى"></i>
                                        </span>
                                    )}

                                    {selectedSkill && (
                                        <span className="badge badge-primary p-2 d-inline-flex align-items-center ml-1" style={{ fontSize: '0.76rem', gap: '0.3rem' }}>
                                            <i className="fas fa-tag"></i>
                                            <span>المهارة: {selectedSkill.name}</span>
                                            <i className="fas fa-times cursor-pointer ml-1" onClick={() => setSelectedSkill(null)} title="إلغاء تصفية المهارة"></i>
                                        </span>
                                    )}

                                    {hasActiveFilters && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger mr-auto"
                                            style={{ fontSize: '0.75rem', borderRadius: '0.45rem' }}
                                            onClick={resetFilters}
                                        >
                                            <i className="fas fa-undo-alt ml-1"></i>{t('Pages.Lessons.Summary.clearFilter') || 'إلغاء كافة التصفيات'}
                                        </button>
                                    )}
                                </div>

                                {/* Rendered Questions List with QuestionView */}
                                <div className="slim-scroll d-flex flex-column pr-1 anim-fade-up" style={{ maxHeight: '680px', overflowY: 'auto', gap: '0.85rem' }}>
                                    {filteredResults.length > 0 ? (
                                        filteredResults.map((r, i) => (
                                            <div key={r.question.Id} className="theme-card summary-card anim-pop mb-2">
                                                <QuestionSummary
                                                    answer={r}
                                                    idx={i}
                                                    fontSize={fontSize}
                                                    onOpenQuestionModal={onOpenQuestion}
                                                    exam={exam}
                                                    hideLevel={isHideLevel}
                                                    ViewGameResult={ViewGameResult}
                                                />
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-5 text-muted bg-light rounded">
                                            <i className="fas fa-filter fa-3x mb-2 text-secondary"></i>
                                            <p className="font-weight-bold mb-1">{t('NoInfo') || 'لا توجد أسئلة تطابق التصفية الحالية'}</p>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary mt-2"
                                                onClick={resetFilters}
                                            >
                                                <i className="fas fa-undo-alt ml-1"></i>{t('Pages.Lessons.Summary.clearFilter') || 'إظهار كافة الأسئلة'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
